/**
 * Creates a safe redirect URL for external links
 * @param {string} url - The external URL to redirect to
 * @returns {string} - The safe redirect URL
 */
export function createSafeRedirectUrl(url) {
  if (!url) return '#';
  
  // If it's already a relative URL or same origin, return as is
  if (url.startsWith('/') || url.startsWith('#') || url.startsWith(window.location.origin)) {
    return url;
  }
  
  // For external URLs, use the safe redirect endpoint
  const encodedUrl = encodeURIComponent(url);
  return `/api/redirect?url=${encodedUrl}`;
}

/**
 * Opens an external URL safely
 * @param {string} url - The URL to open
 * @param {boolean} newTab - Whether to open in a new tab (default: true)
 */
export function openSafeRedirect(url, newTab = true) {
  const safeUrl = createSafeRedirectUrl(url);
  
  if (newTab) {
    window.open(safeUrl, '_blank', 'noopener,noreferrer');
  } else {
    window.location.href = safeUrl;
  }
}

/**
 * Checks if a URL is external (different origin)
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is external
 */
export function isExternalUrl(url) {
  if (!url) return false;
  
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.origin !== window.location.origin;
  } catch (e) {
    // Invalid URL, treat as external to be safe
    return true;
  }
}

/**
 * Gets the display text for a URL (truncated if too long)
 * @param {string} url - The URL to format
 * @param {number} maxLength - Maximum length before truncating
 * @returns {string} - The formatted URL
 */
export function formatUrlForDisplay(url, maxLength = 50) {
  if (!url) return '';
  
  if (url.length <= maxLength) return url;
  
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname;
    const path = urlObj.pathname + urlObj.search;
    
    if (domain.length + 20 > maxLength) {
      return domain.substring(0, maxLength - 3) + '...';
    }
    
    const remainingLength = maxLength - domain.length - 3;
    return domain + '/...' + path.substring(path.length - remainingLength);
  } catch (e) {
    return url.substring(0, maxLength - 3) + '...';
  }
}